/**
 * RankList — sortierte Rangliste mit Anteilsbalken (Top Kunden, Produkte, Außendienst).
 * Balken animieren über transform (scaleX), gestaffelt; reduced-motion respektiert.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Panel, PanelEmpty, PanelSkeleton } from './Panel';

/**
 * @param {Array<{key:string,name:string,value:number,sub?:string}>} rows - bereits sortiert
 * @param {(v:number)=>string} formatValue
 * @param {(row)=>void} [onSelect]
 */
export default function RankList({
  overline, title, meta, rows, loading, formatValue, onSelect,
  initialCount = 5, emptyIcon, emptyTitle, emptyHint,
}) {
  const [expanded, setExpanded] = useState(false);
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;
  const visible = expanded ? rows : rows.slice(0, initialCount);

  return (
    <Panel overline={overline} title={title} meta={meta}>
      {loading ? (
        <PanelSkeleton />
      ) : rows.length === 0 ? (
        <PanelEmpty icon={emptyIcon} title={emptyTitle} hint={emptyHint} />
      ) : (
        <>
          <ol className="divide-y divide-slate-100">
            {visible.map((r, i) => {
              const Row = onSelect ? 'button' : 'div';
              return (
                <li key={r.key}>
                  <Row
                    type={onSelect ? 'button' : undefined}
                    onClick={onSelect ? () => onSelect(r) : undefined}
                    className={cn(
                      'w-full text-left grid grid-cols-[1.75rem_1fr_auto] items-center gap-x-3 py-2.5',
                      onSelect && 'rounded-md -mx-2 px-2 w-[calc(100%+1rem)] hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 active:scale-[0.995] transition-colors'
                    )}
                  >
                    <span
                      className={cn(
                        'font-display text-sm font-extrabold tabular-nums text-center',
                        i === 0 ? 'text-brand-950' : 'text-slate-400'
                      )}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900 truncate">{r.name}</span>
                      <span className="mt-1 block h-1 rounded-full bg-slate-100 overflow-hidden">
                        <span
                          className="block h-full rounded-full bg-brand-950 origin-left motion-safe:animate-[rank-grow_500ms_cubic-bezier(0.16,1,0.3,1)_both]"
                          style={{ transform: `scaleX(${r.value / max})`, animationDelay: `${i * 40}ms`, '--rank-scale': r.value / max }}
                        />
                      </span>
                      {r.sub && <span className="mt-1 block text-[11px] text-slate-400 truncate">{r.sub}</span>}
                    </span>
                    <span className="text-sm font-semibold text-slate-900 tabular-nums text-right">{formatValue(r.value)}</span>
                  </Row>
                </li>
              );
            })}
          </ol>
          {rows.length > initialCount && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 w-full h-9 inline-flex items-center justify-center gap-1 rounded-md text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors"
            >
              {expanded
                ? <><ChevronUp className="w-4 h-4" /> Weniger anzeigen</>
                : <><ChevronDown className="w-4 h-4" /> Alle {rows.length} anzeigen</>}
            </button>
          )}
        </>
      )}
    </Panel>
  );
}
